import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { PlanType } from '@labrav/react-components'
import { get, isEmpty } from 'lodash'
import type {
  Dimension,
  Installments,
  OfferRecipients,
} from '../../../modules/privateOffer/types'
import type { OffersWithErrors } from '../../../modules/privateOffer/privateOfferCreation/types'
import {
  checkIfFormikTouchedArrayHasErr,
  checkIsErrOnOfferDimensions,
  checkIsErrOnOfferInstallments,
  checkIsErrOnRecipientOrStakeHolder,
  isSignleFieldsHaveErr,
} from '../../../common/utils/offerValidationHelper'

export const getIsErrorForOfferTab = (
  tabName: 'offer-details' | 'offer-pricing' | 'offer-other',
  formikValues: FormikProps<OffersWithErrors>,
  eulaErr?: string,
  productType?: PlanType,
  isRenewalPricingTermEnabled?: boolean
) => {
  const { errors, touched } = formikValues
  const isAmi = productType === PlanType.AMI
  if (formikValues?.submitCount <= 0) return false

  switch (tabName) {
    case 'offer-details': {
      const recipentErr =
        checkIfFormikTouchedArrayHasErr(
          formikValues as unknown as FormikProps<Record<string, unknown>>,
          'offerRecipients'
        ) &&
        touched?.offerRecipients?.some((recipientTouched, idx) =>
          checkIsErrOnRecipientOrStakeHolder(
            recipientTouched,
            idx,
            errors?.offerRecipients as unknown as FormikErrors<OfferRecipients>[]
          )
        )
      return (
        isSingleFieldsErr(
          [
            'companyName',
            'awsAccountId',
            'privateOfferName',
            'emailCustomMessage',
            'offerExpirationDate',
            ...(isRenewalPricingTermEnabled
              ? ['renewal', 'renewalChannel']
              : []),
            ...(productType === PlanType.SAAS_SUBSCRIPTION
              ? ['subscriptionEndDate']
              : []),
            ...(productType === PlanType.AMI ? ['offerDescription'] : []),
          ],
          formikValues?.errors
        ) || recipentErr
      )
    }
    case 'offer-pricing': {
      const installmentsWithTouchedErr =
        checkIfFormikTouchedArrayHasErr(
          formikValues as unknown as FormikProps<Record<string, unknown>>,
          'installments'
        ) &&
        (
          touched?.installments as unknown as FormikTouched<Installments>[]
        )?.some((installmentsTouched, idx) =>
          checkIsErrOnOfferInstallments(
            installmentsTouched,
            idx,
            errors?.installments as unknown as FormikErrors<Installments>[]
          )
        )

      const dimensionsWithTouchedErr =
        productType !== PlanType.AMI &&
        checkIfFormikTouchedArrayHasErr(
          formikValues as unknown as FormikProps<Record<string, unknown>>,
          'dimensions'
        ) &&
        (touched?.dimensions as unknown as FormikTouched<Dimension>[])?.some(
          (dimensionTouched, idx) =>
            checkIsErrOnOfferDimensions(
              dimensionTouched,
              idx,
              errors?.dimensions as unknown as FormikErrors<Dimension>[]
            )
        )

      return (
        isSignleFieldsHaveErr(
          [
            ...(productType !== PlanType.AMI
              ? [
                  'serviceLength',
                  'containerOfferServiceLength',
                  'usageDuration',
                ]
              : []),
            ...(formikValues?.values?.variableStartDate === false
              ? ['agreementStartDate']
              : []),
            ...(productType === PlanType.SAAS_CONTRACT
              ? ['entitlementOption']
              : []),

            ...((formikValues?.values?.pricingModel === 'installment-plan' ||
              formikValues?.values?.pricingModel === 'upfront-payment') &&
            isAmi
              ? ['durationType', 'durationLength']
              : []),
            ...((formikValues?.values?.pricingModel === 'upfront-payment' ||
              formikValues?.values?.pricingModel === 'usage-pricing') &&
            isAmi
              ? ['usageEndDate']
              : []),
          ],
          formikValues?.errors as FormikErrors<Record<string, unknown>>,
          formikValues?.touched as FormikTouched<Record<string, unknown>>
        ) ||
        installmentsWithTouchedErr ||
        dimensionsWithTouchedErr
      )
    }
    case 'offer-other': {
      return (
        isSingleFieldsErr(
          [
            'metaData',
            'offerStakeholders',
            'eulaType',
            'customOfferStakeholders',
            'eulaFile',
            ...(!isRenewalPricingTermEnabled
              ? ['renewal', 'renewalChannel']
              : []),
          ],
          formikValues?.errors
        ) || !isEmpty(eulaErr)
      )
    }
    default: {
      return false
    }
  }
}

export const isSingleFieldsErr = (
  singleFieldArray: string[],
  errors: FormikErrors<OffersWithErrors>
) => {
  return singleFieldArray.some(
    singleFieldItem => !isEmpty(get(errors, singleFieldItem))
  )
}
